import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Heart, 
  Phone, 
  MapPin, 
  Clock, 
  AlertTriangle,
  User,
  Calendar,
  FileText,
  MessageCircle,
  Building2,
  X
} from 'lucide-react';
import api from '../utils/api';
import LoadingSpinner from '../components/common/LoadingSpinner';
import HospitalMap from '../components/emergency/HospitalMap';
import AIChatbot from '../components/chatbot/AIChatbot';

const Emergency = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showChatbot, setShowChatbot] = useState(false);
  const [showMap, setShowMap] = useState(false);
  const [scanId] = useState(new URLSearchParams(window.location.search).get('scanId'));

  useEffect(() => {
    fetchProfile();
  }, [token, scanId]);

  const fetchProfile = async () => {
    try {
      setLoading(true);
      const url = scanId ? `/emergency/${token}?scanId=${scanId}` : `/emergency/${token}`;
      const response = await api.get(url);
      
      if (response.success) {
        setProfile(response.profile);
      } else {
        setError(response.message || 'Failed to load profile');
      }
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to load emergency profile');
    } finally {
      setLoading(false);
    }
  };

  const makeCall = (phoneNumber) => {
    window.open(`tel:${phoneNumber}`, '_self');
  };

  const getAge = (dateOfBirth) => {
    if (!dateOfBirth) return 'Unknown';
    const today = new Date();
    const birthDate = new Date(dateOfBirth);
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-red-50">
        <div className="text-center">
          <LoadingSpinner size="large" />
          <p className="mt-4 text-lg text-gray-600">Loading emergency profile...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-red-50">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
          <AlertTriangle className="h-16 w-16 text-red-600 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Access Denied</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={() => navigate('/scan')}
            className="btn-primary"
          >
            Scan Another QR Code
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-red-50">
      {/* Emergency Header */}
      <div className="bg-red-600 text-white p-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <Heart className="h-6 w-6" />
            <span className="text-lg font-semibold">Emergency Medical Profile</span>
          </div>
          <div className="flex items-center space-x-4">
            <button
              onClick={() => setShowMap(!showMap)}
              className={`px-4 py-2 rounded-lg transition-colors ${
                showMap ? 'bg-white text-red-600' : 'bg-red-700 hover:bg-red-800'
              }`}
            >
              <Hospital className="h-5 w-5 inline mr-2" />
              Nearby Hospitals
            </button>
            <button
              onClick={() => setShowChatbot(!showChatbot)}
              className={`px-4 py-2 rounded-lg transition-colors ${
                showChatbot ? 'bg-white text-red-600' : 'bg-red-700 hover:bg-red-800'
              }`}
            >
              <MessageCircle className="h-5 w-5 inline mr-2" />
              AI Assistant
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Profile Info */}
          <div className="lg:col-span-2 space-y-6">
            {/* Patient Info Card */}
            <div className="bg-white rounded-xl shadow-lg p-6">
              <div className="flex items-start justify-between mb-6">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-2">
                    {profile.fullName}
                  </h2>
                  <div className="flex items-center space-x-4 text-gray-600">
                    <div className="flex items-center space-x-1">
                      <Calendar className="h-4 w-4" />
                      <span>Age: {getAge(profile.dateOfBirth)}</span>
                    </div>
                    <div className="flex items-center space-x-1">
                      <User className="h-4 w-4" />
                      <span>{profile.gender}</span>
                    </div>
                  </div>
                </div>
                <div className="blood-group">
                  {profile.bloodGroup}
                </div>
              </div>

              {/* Critical Information */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Allergies */}
                <div className="bg-red-50 rounded-lg p-4">
                  <h3 className="font-semibold text-red-800 mb-2 flex items-center">
                    <AlertTriangle className="h-5 w-5 mr-2" />
                    Allergies
                  </h3>
                  {profile.allergies && profile.allergies.length > 0 ? (
                    <ul className="space-y-1">
                      {profile.allergies.map((allergy, index) => (
                        <li key={index} className="text-red-700">• {allergy}</li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-red-600">No known allergies</p>
                  )}
                </div>

                {/* Chronic Conditions */}
                <div className="bg-yellow-50 rounded-lg p-4">
                  <h3 className="font-semibold text-yellow-800 mb-2">
                    <FileText className="h-5 w-5 inline mr-2" />
                    Chronic Conditions
                  </h3>
                  {profile.chronicConditions && profile.chronicConditions.length > 0 ? (
                    <ul className="space-y-1">
                      {profile.chronicConditions.map((condition, index) => (
                        <li key={index} className="text-yellow-700">• {condition}</li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-yellow-600">No chronic conditions</p>
                  )}
                </div>
              </div>

              {/* Current Medications */}
              {profile.currentMedications && profile.currentMedications.length > 0 && (
                <div className="bg-blue-50 rounded-lg p-4 mt-6">
                  <h3 className="font-semibold text-blue-800 mb-3">Current Medications</h3>
                  <div className="space-y-2">
                    {profile.currentMedications.map((med, index) => (
                      <div key={index} className="text-blue-700">
                        <span className="font-medium">{med.name}</span>
                        {med.dosage && <span className="ml-2">({med.dosage})</span>}
                        {med.frequency && <span className="ml-2 text-sm">- {med.frequency}</span>}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Medical Notes */}
              {profile.medicalNotes && (
                <div className="bg-gray-50 rounded-lg p-4 mt-6">
                  <h3 className="font-semibold text-gray-800 mb-2">Medical Notes</h3>
                  <p className="text-gray-700">{profile.medicalNotes}</p>
                </div>
              )}

              {/* Organ Donor Status */}
              <div className="mt-6 flex items-center space-x-2">
                <Heart className={`h-5 w-5 ${profile.organDonor ? 'text-green-600' : 'text-gray-400'}`} />
                <span className="font-medium">
                  {profile.organDonor ? 'Organ Donor' : 'Not an Organ Donor'}
                </span>
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Emergency Contacts */}
            <div className="bg-white rounded-xl shadow-lg p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <Phone className="h-5 w-5 mr-2" />
                Emergency Contacts
              </h3>
              <div className="space-y-3">
                {profile.emergencyContacts.map((contact, index) => (
                  <div key={index} className="emergency-contact">
                    <div className="flex items-start justify-between">
                      <div className="flex-grow">
                        <div className="font-medium text-gray-900">{contact.name}</div>
                        <div className="text-sm text-gray-600">{contact.relation}</div>
                        {contact.email && (
                          <div className="text-sm text-gray-500">{contact.email}</div>
                        )}
                      </div>
                      {contact.phone && (
                        <button
                          onClick={() => makeCall(contact.phone)}
                          className="btn-primary text-sm px-3 py-1"
                        >
                          <Phone className="h-4 w-4 inline mr-1" />
                          Call
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Emergency Services */}
            <div className="bg-red-600 text-white rounded-xl shadow-lg p-6">
              <h3 className="text-lg font-semibold mb-4">Emergency Services</h3>
              <div className="space-y-3">
                <button
                  onClick={() => makeCall('911')}
                  className="w-full bg-white text-red-600 px-4 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors"
                >
                  Call 911
                </button>
                <button
                  onClick={() => makeCall('112')}
                  className="w-full bg-red-700 text-white px-4 py-3 rounded-lg font-semibold hover:bg-red-800 transition-colors"
                >
                  Call 112 (EU Emergency)
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Hospital Map Modal */}
      {showMap && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-6xl w-full max-h-[90vh] overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-lg font-semibold">Nearby Hospitals</h3>
              <button
                onClick={() => setShowMap(false)}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="p-4">
              <HospitalMap />
            </div>
          </div>
        </div>
      )}

      {/* AI Chatbot Modal */}
      {showChatbot && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-lg font-semibold">AI Medical Assistant</h3>
              <button
                onClick={() => setShowChatbot(false)}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="h-96">
              <AIChatbot />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Emergency;
